/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import type { Mention, Registre } from '@/types';

import {
  formatAdressePrefixee,
  formatCoordonneesRegistre,
  formatDate,
  formatDateNaissance,
  formatLieuIndividu,
  formatLieuIndividuPrefixe,
  formatMentions,
  formatParent,
  formatProfession,
  formatSexe,
} from './formatters';

/**
 * Construit le payload de `POST /numerisation-actes/deces`, au contrat de
 * `ActeNumDecesController::storeDeces` :
 * `{ acte, defunt, parent1, parent2, declarant, mentions }`.
 *
 * ⚠️ Trois répartitions contre-intuitives, toutes voulues :
 *
 * 1. **état matrimonial et ex-conjoint dans `defunt`**, alors que `ec_actd` les
 *    porte : le contrat suit le domaine, le repository fait le partage
 *    (`extraireChampsPortesParActe`).
 * 2. **lieu de décès dans `defunt`** (bloc à plat `deces*`), alors qu'il est
 *    saisi dans le bloc « évènement » : l'acte n'a pas de colonne de lieu.
 * 3. **date de décès dans `acte`**, que le back recopie sur le défunt.
 *
 * Fonction pure, testable sans React.
 */
const buildActeDecesPayload = (
  data: Record<string, any>,
  mentions: Mention[],
  selectedRegistre?: Partial<Registre>,
) => ({
  acte: {
    ...formatCoordonneesRegistre(selectedRegistre, data?.dateActe),
    numeroActe: data?.numeroActe,
    numeroOrdre: data?.ordreActe,
    acteNumeroComplement: data?.complementActe,
    heureDresseActe: data?.heureActe,
    minuteDresseActe: data?.minuteActe,
    identifiantSousTypeActe: data?.sousTypeActe,
    dateDeces: formatDate(data?.dateDeces),
    heureDeces: data?.heureDeces,
    minuteDeces: data?.minuteDeces,
  },
  defunt: {
    nomFamille: data?.nomFamille,
    nomUsage: data?.nomUsage,
    prenom: data?.prenom,
    prenomsComplementaires: data?.prenomsComplementaires,
    naissanceDate: formatDateNaissance(data),
    ...formatLieuIndividu(data?.adresseNaissance),
    // Lieu de décès et domicile : blocs à plat de l'individu, cf. §2 du JSDoc.
    ...formatLieuIndividuPrefixe(data?.adresseDeces, 'deces'),
    ...formatAdressePrefixee(data?.adresseDomicile, 'domicile'),
    profession: formatProfession(data?.profession),
    ...(!_.isNil(formatSexe(data?.sexe)) && { sexe: formatSexe(data?.sexe) }),
    // Portés par l'acte, envoyés ici, cf. §1 du JSDoc.
    etatMatrimonialCode: data?.etatMatrimonialCode,
    etatMatrimonialLibelle: data?.etatMatrimonialLibelle,
    // Propre au seul état « pacsé » ; le back purge sur toute autre situation.
    etatMatrimonialCodePacs: data?.etatMatrimonialCodePacs,
    dateEtatMatrimonial: formatDate(data?.dateEtatMatrimonial),
    // `ad_df_sexex` stocke une LETTRE ('M'/'F'), pas le code INSEE 1/2 : la
    // saisie se fait déjà dans cette représentation (`OPTIONS_SEXE_LETTRE`),
    // il n'y a donc rien à convertir.
    sexeExConjoint: data?.sexeExConjoint,
    nomExConjoint: data?.nomExConjoint,
    prenomExConjoint: data?.prenomExConjoint,
  },
  /*
   * Père et mère du DÉFUNT, au sens `decede` (comme la naissance, et non
   * `inconnu` comme la reconnaissance) : c'est bien « décédé » que la base
   * enregistre pour eux, et l'état civil du parent est conservé
   * (cf. `formatParent`).
   */
  parent1: formatParent(data?.parent1, data?.filiationInconnueParent1),
  parent2: formatParent(data?.parent2, data?.filiationInconnueParent2),
  declarant: {
    nomFamille: data?.declarant?.nomFamille,
    prenom: data?.declarant?.prenom,
    prenomsComplementaires: data?.declarant?.prenomsComplementaires,
    // `ai_age` est un entier : le champ de saisie produit une chaîne.
    ...(!_.isEmpty(_.toString(data?.declarant?.age)) && {
      age: Number(data.declarant.age),
    }),
    profession: formatProfession(data?.declarant?.profession),
    ...formatAdressePrefixee(data?.declarant?.adresseDomicile, 'domicile'),
  },
  ...(!_.isEmpty(mentions) && { mentions: formatMentions(mentions) }),
});

export default buildActeDecesPayload;
